export interface FixedStepAccumulator {
  step: number
  maxFrame: number
  maxSteps: number
  carry: number
}

interface FixedStepAccumulatorOptions {
  step?: number
  maxFrame?: number
  maxSteps?: number
}

export interface FixedStepResult {
  steps: number
  stepDt: number
  alpha: number
  clampedFrameDt: number
}

export function createFixedStepAccumulator(
  options: FixedStepAccumulatorOptions = {},
): FixedStepAccumulator {
  const step = options.step ?? 1 / 60
  const maxFrame = options.maxFrame ?? 0.05
  const maxSteps = options.maxSteps ?? 8

  return {
    step: step > 0 ? step : 1 / 60,
    maxFrame: maxFrame > 0 ? maxFrame : 0.05,
    maxSteps: maxSteps > 0 ? Math.floor(maxSteps) : 8,
    carry: 0,
  }
}

export function resetAccumulator(accumulator: FixedStepAccumulator) {
  accumulator.carry = 0
}

export function advanceFixedStep(
  accumulator: FixedStepAccumulator,
  frameDt: number,
): FixedStepResult {
  const clampedFrameDt = Math.max(0, Math.min(frameDt, accumulator.maxFrame))
  accumulator.carry += clampedFrameDt

  let steps = 0
  while (accumulator.carry >= accumulator.step && steps < accumulator.maxSteps) {
    accumulator.carry -= accumulator.step
    steps++
  }

  // Drop stale carry after max step budget is consumed.
  if (steps === accumulator.maxSteps && accumulator.carry > accumulator.step) {
    accumulator.carry = accumulator.step
  }

  return {
    steps,
    stepDt: accumulator.step,
    alpha: accumulator.step > 0 ? accumulator.carry / accumulator.step : 0,
    clampedFrameDt,
  }
}
